import { Client, Coach, DecoyClient } from "@/types/page";
import classNames from "@/utils/classNames";
import { Link } from "@/utils/navigation";
import { Menu, Transition } from "@headlessui/react";
import { useMediaQuery } from "@react-hook/media-query";
import { Fragment, ReactElement } from "react";
import { FaBars, FaChevronDown } from "react-icons/fa";

export type ProfileMenuItem = {
    name: string;
    icon?: ReactElement;
    selected?: boolean;
} & ({ href: string; openExternal?: boolean } | { onClick: () => void });

type ProfileIconProps = {
    user: Client | Coach | DecoyClient;
    profileMenuItems: ProfileMenuItem[];
    imageSize?: number;
    menuItemsClassNames?: string;
    nameClassNames?: string;
};

export default function ProfileIcon({
    user,
    profileMenuItems,
    imageSize = 32,
    menuItemsClassNames = "",
    nameClassNames = "text-base",
}: ProfileIconProps) {
    const smallDisplay = useMediaQuery("(min-width: 640px)");

    return (
        <Menu as="div" className="relative">
            <div>
                <Menu.Button className="relative flex text-sm text-brand-black">
                    <span
                        data-testid="header-nick-name"
                        className="absolute -inset-1.5"
                    />
                    <span className="sr-only">Open user menu</span>
                    {user.type === "coach" ? (
                        <img
                            className="rounded-full object-cover"
                            style={{
                                width: imageSize,
                                height: imageSize,
                            }}
                            src={user.profile_photo_url}
                            alt="User profile"
                        />
                    ) : (
                        <div
                            className={classNames(
                                "flex items-center",
                                nameClassNames,
                            )}
                        >
                            {smallDisplay ? (
                                <>
                                    {user.first_name} {user.last_name}
                                    <FaChevronDown className="ml-2" />
                                </>
                            ) : (
                                <FaBars className="text-xl" />
                            )}
                        </div>
                    )}
                </Menu.Button>
            </div>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items
                    data-testid="more-options-list"
                    className={classNames(
                        "absolute z-30 mt-2 flex w-52 origin-top-right flex-col rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none",
                        menuItemsClassNames,
                    )}
                >
                    {profileMenuItems.map((item) => (
                        <ProfileMenuEntry key={item.name} item={item} />
                    ))}
                </Menu.Items>
            </Transition>
        </Menu>
    );
}

type ProfileMenuEntryProps = {
    item: ProfileMenuItem;
};

function ProfileMenuEntry({ item }: ProfileMenuEntryProps) {
    const className = classNames(
        "flex gap-1.5 items-center px-4 py-2 text-sm text-left font-medium hover:bg-brand-lightGray",
        item.selected ? "text-brand-red" : "text-brand-black",
    );
    const testId = `more-options-${item.name.replace(/\s+/g, "-").toLowerCase()}`;

    return "onClick" in item ? (
        <Menu.Item
            as="button"
            type="button"
            onClick={item.onClick}
            className={className}
            data-testid={testId}
        >
            {item.icon}
            {item.name}
        </Menu.Item>
    ) : item.openExternal ? (
        <Menu.Item
            as="a"
            href={item.href}
            target="_blank"
            rel="noopener"
            className={className}
            data-testid={testId}
        >
            {item.icon}
            {item.name}
        </Menu.Item>
    ) : (
        <Menu.Item
            as={Link}
            href={item.href}
            className={className}
            data-testid={testId}
        >
            {item.icon}
            {item.name}
        </Menu.Item>
    );
}
